import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, Filter, Trash2, CheckSquare, Square, ArrowLeft, Send } from 'lucide-react';
import toast from 'react-hot-toast';

function OrganizeNumbers() {
    const navigate = useNavigate();
    const location = useLocation();

    // Data passed from BulkMessage.js via navigate(path, { state })
    const incoming = location.state?.contacts || [];
    const columnType = location.state?.columnType || 'none'; // 'none' | 'category' | 'name'

    // Local working copy — each entry gets a `selected` flag
    const [entries, setEntries] = useState(
        incoming.map((c) => ({ ...c, selected: true }))
    );
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Distinct categories (only meaningful when columnType === 'category')
    const categories = useMemo(() => {
        if (columnType !== 'category') return [];
        const set = new Set();
        entries.forEach((e) => { if (e.category) set.add(e.category); });
        return Array.from(set).sort();
    }, [entries, columnType]);

    const visibleEntries = useMemo(() => {
        if (categoryFilter === 'all') return entries;
        return entries.filter((e) => e.category === categoryFilter);
    }, [entries, categoryFilter]);

    const selectedCount = entries.filter((e) => e.selected).length;
    const visibleSelectedCount = visibleEntries.filter((e) => e.selected).length;


    const toggleOne = (phone) => {
        setEntries((prev) =>
            prev.map((e) => (e.phone === phone ? { ...e, selected: !e.selected } : e))
        );
    };

    const toggleAllVisible = (checked) => {
        const visiblePhones = new Set(visibleEntries.map((e) => e.phone));
        setEntries((prev) =>
            prev.map((e) => (visiblePhones.has(e.phone) ? { ...e, selected: checked } : e))
        );
    };

    const allVisibleSelected = visibleEntries.length > 0 && visibleEntries.every((e) => e.selected);

    const deleteOne = (phone) => {
        setEntries((prev) => prev.filter((e) => e.phone !== phone));
    };

    const deleteUnselected = () => {
        if (!window.confirm('Remove all unselected numbers from this list?')) return;
        setEntries((prev) => prev.filter((e) => e.selected));
        toast.success('Unselected numbers removed');
    };

    const handleSubmit = () => {
        // Sirf currently visible (filtered) aur selected numbers hi submit honge
        const confirmed = visibleEntries
            .filter((e) => e.selected)
            .map(({ selected, ...rest }) => rest);

        if (confirmed.length === 0) {
            toast.error('Select at least one number before submitting');
            return;
        }

        toast.success(`${confirmed.length} number(s) confirmed`);
        navigate('/bulk-message', { state: { confirmedRecipients: confirmed } });
    };

    const handleCancel = () => {
        navigate('/bulk-message');
    };

    if (incoming.length === 0) {
        return (
            <div className="dash-page">
                <div className="dash-header">
                    <div>
                        <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Users size={26} color="#1e3a5f" /> Organize Numbers
                        </h1>
                    </div>
                </div>
                <div className="dash-card">
                    <div className="empty-state">
                        <div className="empty-state-icon"><Users size={56} color="#cbd5e1" /></div>
                        <h3 className="empty-state-title">No Data to Organize</h3>
                        <p className="empty-state-description">Go back and process some numbers first.</p>
                        <button onClick={handleCancel} className="settings-btn-save" style={{ marginTop: '16px' }}>
                            <ArrowLeft size={15} /> Back to Bulk Message
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dash-page">
            <div className="dash-header">
                <div>
                    <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Users size={26} color="#1e3a5f" /> Organize Numbers
                    </h1>
                    <p className="dash-subtitle">Filter, select, or remove numbers before processing them for your campaign</p>
                </div>
            </div>

            <div className="dash-card mb-lg">
                <div className="settings-tab-header">
                    <h2 className="settings-tab-title">
                        {entries.length} total · {selectedCount} selected
                    </h2>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
                    {columnType === 'category' && categories.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Filter size={16} color="#64748b" />
                            <select
                                className="settings-form-input"
                                style={{ minWidth: '180px' }}
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <option value="all">All Categories</option>
                                {categories.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <button
                        onClick={() => toggleAllVisible(!allVisibleSelected)}
                        className="settings-btn-test"
                        type="button"
                    >
                        {allVisibleSelected ? <><Square size={15} /> Deselect All</> : <><CheckSquare size={15} /> Select All</>}
                    </button>

                    <button
                        onClick={deleteUnselected}
                        className="settings-btn-remove"
                        type="button"
                    >
                        <Trash2 size={15} /> Remove Unselected
                    </button>
                </div>

                <div className="table-container table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th>Phone</th>
                                {columnType === 'name' && <th>Name</th>}
                                {columnType === 'category' && <th>Category</th>}
                                <th style={{ width: '60px' }}>Remove</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleEntries.map((e) => (
                                <tr key={e.phone}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={e.selected}
                                            onChange={() => toggleOne(e.phone)}
                                        />
                                    </td>
                                    <td>{e.phone}</td>
                                    {columnType === 'name' && <td>{e.name || '—'}</td>}
                                    {columnType === 'category' && <td>{e.category || '—'}</td>}
                                    <td>
                                        <button
                                            onClick={() => deleteOne(e.phone)}
                                            className="btn btn-info btn-sm"
                                            style={{ background: '#ef4444' }}
                                            title="Remove"
                                            type="button"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {visibleEntries.length === 0 && (
                    <div className="empty-state">
                        <p className="empty-state-description">No numbers match this filter.</p>
                    </div>
                )}
            </div>

            <div className="dash-card" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="settings-btn-test" type="button">
                    <ArrowLeft size={15} /> Cancel
                </button>
                <button onClick={handleSubmit} className="settings-btn-save" type="button">
                    <Send size={15} /> Submit ({categoryFilter === 'all' ? selectedCount : visibleSelectedCount})
                </button>
            </div>
        </div>
    );
}

export default OrganizeNumbers;